// A Clubhouse TV view for the marketing page: the same payload shape the API
// sends a real screen (ClubDisplayViewResponse), filled with a made-up club,
// so TvScreen runs exactly as it does on a clubhouse wall. Nothing here is a
// real club, player or result.
import {TV_PANEL} from '~/utils/tvDisplay';

const hoursFromNow = (hours) => new Date(Date.now() + hours * 3_600_000).toISOString();
const at = (daysAhead, hour, minute = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  date.setHours(hour, minute, 0, 0);
  return date.toISOString();
};

const id = (n) => `00000000-0000-0000-0000-${String(n).padStart(12, '0')}`;

// Kinds the API uses on results: 0 league, 1 ladder, 2 tournament, 3 friendly, 4 meet, 5 americano
const result = (n, home, away, games, competitionName, competitionKind, hoursAgo, provisional = false) => {
  const homeWon = games.filter(([h, a]) => h > a).length;
  const awayWon = games.length - homeWon;
  return {
    fixtureId: id(n),
    playedAt: hoursFromNow(-hoursAgo),
    homeTeamName: home,
    awayTeamName: away,
    homeResult: String(homeWon),
    awayResult: String(awayWon),
    homePoints: homeWon > awayWon ? 3 : 0,
    awayPoints: awayWon > homeWon ? 3 : 0,
    games: games.map(([h, a]) => ({home: h, away: a})),
    competitionName,
    competitionKind,
    provisional,
  };
};

const RESULTS = [
  result(101, 'Priya Khan', 'Alex Morgan', [[11, 8], [7, 11], [11, 9], [11, 6]], 'Club Championship · Division 1', 0, 1),
  result(102, 'Dana Reed', 'Sam Taylor', [[11, 4], [11, 7], [11, 9]], 'Singles Ladder', 1, 3),
  result(103, 'Lee Walker + Mo Hassan', 'Chris Dale + Jo Park', [[6, 3], [4, 6], [6, 2]], 'Summer Open', 2, 5),
  result(104, 'Sofia Marino', 'Jack Turner', [[11, 9], [9, 11], [11, 7]], 'Club Championship · Division 2', 0, 7, true),
  result(105, 'Leo Fernandez', 'Priya Patel', [[11, 5], [11, 8], [8, 11], [11, 6]], 'Club Championship · Division 2', 0, 22),
  result(106, 'Alex Morgan', 'Dana Reed', [[11, 13], [11, 7], [11, 9], [12, 10]], 'Singles Ladder', 1, 26),
  result(107, 'Jo Park', 'Mo Hassan', [[11, 2], [11, 4], [11, 3]], 'Wednesday Night Mixer', 4, 30),
  result(108, 'Sam Taylor', 'Chris Dale', [[11, 8], [11, 9], [6, 11], [11, 7]], 'Club Championship · Division 1', 0, 48),
];

const UPCOMING = [
  {fixtureId: id(201), scheduledAt: hoursFromNow(1.5), homeTeamName: 'Alex Morgan', awayTeamName: 'Sam Taylor', competitionName: 'Club Championship · Division 1', competitionKind: 0, playingAreaName: 'Court 2'},
  {fixtureId: id(202), scheduledAt: hoursFromNow(2.5), homeTeamName: 'Dana Reed', awayTeamName: 'Priya Khan', competitionName: 'Singles Ladder', competitionKind: 1, playingAreaName: 'Court 1'},
  {fixtureId: id(203), scheduledAt: at(1, 19, 30), homeTeamName: 'Sofia Marino', awayTeamName: 'Leo Fernandez', competitionName: 'Club Championship · Division 2', competitionKind: 0, playingAreaName: 'Court 3'},
];

// A match being scored right now: the state shape the member scoreboard writes
const liveState = JSON.stringify({
  path: [{unit: 'match', home: 1, away: 1}, {unit: 'game', home: 7, away: 5}],
  completedUnits: {game: [{unit: 'game', home: 11, away: 8}, {unit: 'game', home: 9, away: 11}]},
});
const LIVE = [{
  sessionId: id(301), fixtureId: id(302), startedAt: hoursFromNow(-0.4),
  homeTeamName: 'Chris Dale', awayTeamName: 'Lee Walker',
  competitionName: 'Club Championship · Division 1', competitionKind: 0, playingAreaName: 'Court 1',
  schema: '{}', state: liveState, lastAppliedSequence: 41,
}];

const standingsRow = (position, teamName, played, points, bonusPoints) =>
    ({position, teamName, played, points, bonusPoints, totalPoints: points + bonusPoints});

const STANDINGS = {
  playersPerTeam: 1,
  name: 'Division 1',
  cycleName: 'Autumn 2026',
  rows: [
    standingsRow(1, 'Priya Khan', 5, 13, 2),
    standingsRow(2, 'Alex Morgan', 5, 12, 1),
    standingsRow(3, 'Sam Taylor', 4, 9, 2),
    standingsRow(4, 'Chris Dale', 4, 8, 1),
    standingsRow(5, 'Lee Walker', 5, 6, 2),
    standingsRow(6, 'Dana Reed', 3, 4, 0),
  ],
};

const rung = (position, teamName, oldPosition, gamesPlayed, extra = {}) =>
    ({position, oldPosition, teamName, gamesPlayed, lastPlayed: hoursFromNow(-gamesPlayed * 30), outgoingChallenges: 0, incomingChallenges: 0, frozen: false, ...extra});

const LADDER = {
  playersPerTeam: 1,
  rows: [
    rung(1, 'Dana Reed', 1, 9, {incomingChallenges: 1}),
    rung(2, 'Alex Morgan', 3, 7, {outgoingChallenges: 1}),
    rung(3, 'Sam Taylor', 2, 8),
    rung(4, 'Morgan Lee', 4, 2, {frozen: true}),
    rung(5, 'Priya Khan', 5, 6, {incomingChallenges: 1, outgoingChallenges: 1}),
    rung(6, 'Lee Walker', 6, 5),
    rung(7, 'Jo Park', 8, 3),
    rung(8, 'Mo Hassan', 7, 4),
  ],
};

const drawFixture = (n, home, away, games, sort) => {
  const r = games ? result(n, home, away, games, 'Summer Open', 2, 12) : null;
  return {
    homeTeamId: id(n * 10 + 1), awayTeamId: id(n * 10 + 2),
    homeTeamName: home, awayTeamName: away,
    homeResult: r?.homeResult ?? null, awayResult: r?.awayResult ?? null,
    homePoints: r?.homePoints ?? null, awayPoints: r?.awayPoints ?? null,
    games: r?.games ?? null, sort,
    playedAt: r ? r.playedAt : null, scheduledAt: r ? null : at(2, 14),
  };
};

// StageType 1 = group, 2 = knockout; the groups are done, so the bracket shows
const DRAW = {
  playersPerTeam: 2,
  stages: [
    {name: 'Quarter-finals', type: 2, round: 3, fixtures: [
      drawFixture(401, 'L. Walker + M. Hassan', 'C. Dale + J. Park', [[6, 3], [4, 6], [6, 2]], 1),
      drawFixture(402, 'D. Reed + A. Morgan', 'S. Marino + J. Turner', [[6, 4], [6, 4]], 2),
      drawFixture(403, 'P. Khan + S. Taylor', 'L. Fernandez + P. Patel', [[7, 5], [6, 7], [6, 3]], 3),
      drawFixture(404, 'M. Lee + K. Osei', 'R. Singh + T. Nguyen', [[6, 1], [6, 2]], 4),
    ]},
    {name: 'Semi-finals', type: 2, round: 2, fixtures: [
      drawFixture(405, 'L. Walker + M. Hassan', 'D. Reed + A. Morgan', [[6, 4], [3, 6], [6, 4]], 1),
      drawFixture(406, 'P. Khan + S. Taylor', 'M. Lee + K. Osei', null, 2),
    ]},
    {name: 'Final', type: 2, round: 1, fixtures: [
      drawFixture(407, 'L. Walker + M. Hassan', null, null, 1),
    ]},
  ],
};

const americanoRow = (position, playerName, played, points, pointsAgainst, setsDiff, gamesDiff) =>
    ({position, playerName, played, points, pointsAgainst, setsFor: 0, setsAgainst: 0, pointsDiff: points - pointsAgainst, setsDiff, gamesDiff});

const AMERICANO = {
  name: 'Group A',
  cycleName: 'Round 3',
  hasSetScores: true,
  rows: [
    americanoRow(1, 'Sofia Marino', 3, 3, 0, 5, 14),
    americanoRow(2, 'Jack Turner', 3, 2, 1, 2, 6),
    americanoRow(3, 'Priya Patel', 3, 1, 2, -2, -7),
    americanoRow(4, 'Leo Fernandez', 3, 0, 3, -5, -13),
  ],
};

const STATS = {
  contextName: 'This season',
  rows: [
    {position: 1, playerName: 'Dana Reed', played: 52, wins: 41, draws: 0, losses: 11, winPercentage: 0.79},
    {position: 2, playerName: 'Alex Morgan', played: 53, wins: 39, draws: 0, losses: 14, winPercentage: 0.74},
    {position: 3, playerName: 'Priya Khan', played: 51, wins: 34, draws: 0, losses: 17, winPercentage: 0.67},
    {position: 4, playerName: 'Sam Taylor', played: 52, wins: 30, draws: 0, losses: 22, winPercentage: 0.58},
    {position: 5, playerName: 'Lee Walker', played: 49, wins: 24, draws: 0, losses: 25, winPercentage: 0.49},
    {position: 6, playerName: 'Chris Dale', played: 44, wins: 20, draws: 0, losses: 24, winPercentage: 0.45},
  ],
};

const CHALLENGES = [
  {ladderName: 'Singles Ladder', challengerName: 'Alex Morgan', challengedName: 'Dana Reed', accepted: true, when: hoursFromNow(2.5), createdAt: hoursFromNow(-20), chanceOfSuccess: 42},
  {ladderName: 'Singles Ladder', challengerName: 'Priya Khan', challengedName: 'Sam Taylor', accepted: false, when: null, createdAt: hoursFromNow(-5), chanceOfSuccess: 55},
  {ladderName: 'Singles Ladder', challengedName: 'Priya Khan', challengerName: 'Lee Walker', accepted: true, when: at(2, 18, 30), createdAt: hoursFromNow(-30), chanceOfSuccess: 38},
];

const gridTeams = STANDINGS.rows.slice(0, 5).map((row, i) => ({id: id(500 + i), position: row.position, name: row.teamName, played: row.played, bonusPoints: row.bonusPoints, totalPoints: row.totalPoints}));
const gridCells = [];
const gridScores = {
  '0-1': ['3-1', 'W'], '1-0': ['1-3', 'L'], '0-2': ['3-2', 'W'], '2-0': ['2-3', 'L'], '1-2': ['3-0', 'W'], '2-1': ['0-3', 'L'],
  '3-4': ['3-1', 'W'], '4-3': ['1-3', 'L'], '0-3': ['3-0', 'W'], '3-0': ['0-3', 'L'], '2-4': ['3-2', 'W'], '4-2': ['2-3', 'L'],
};
for (const [key, [score, outcome]] of Object.entries(gridScores)) {
  const [r, c] = key.split('-').map(Number);
  gridCells.push({rowTeamId: gridTeams[r].id, columnTeamId: gridTeams[c].id, score, outcome});
}
const GRID = {playersPerTeam: 1, teams: gridTeams, cells: gridCells};

const REPORTS = [
  {result: RESULTS[0], report: 'Khan took the opener with sharp length and never let Morgan settle. Morgan levelled through the second with some brave volleying, but Khan\'s drop shot did the damage in the third and fourth to close it out in four.'},
  {result: RESULTS[1], report: 'A commanding display from Reed, who moved Taylor around the court from the first rally and kept the errors to a minimum. Straight games, and the top of the ladder stays where it is.'},
];

// A stand-in for the QR our API draws from the join link: a finder pattern
// and a deterministic grid, so the tile reads as a code without being one.
const qrSvg = (seed) => {
  const n = 25;
  let s = seed;
  const rand = () => { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff; };
  const cells = [];
  const finder = (x, y) => {
    for (let i = 0; i < 7; i++) for (let j = 0; j < 7; j++) {
      const edge = i === 0 || i === 6 || j === 0 || j === 6;
      const core = i >= 2 && i <= 4 && j >= 2 && j <= 4;
      if (edge || core) cells.push(`<rect x="${x + i}" y="${y + j}" width="1" height="1"/>`);
    }
  };
  finder(0, 0); finder(n - 7, 0); finder(0, n - 7);
  for (let x = 0; x < n; x++) for (let y = 0; y < n; y++) {
    const inFinder = (x < 8 && y < 8) || (x >= n - 8 && y < 8) || (x < 8 && y >= n - 8);
    if (!inFinder && rand() < 0.45) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="#fff"/><g fill="#14181f">${cells.join('')}</g></svg>`;
};

const joinCode = (seed, path) => ({
  url: `https://tapleagues.com/invite/${path}`,
  displayUrl: `tapleagues.com/invite/${path}`,
  svg: qrSvg(seed),
  needsApproval: false,
  membersOnly: false,
});

const panel = (key, kind, title, extra = {}, {seconds = 12, pinned = false, sort = 0, subtitle = null, sportName = 'Squash'} = {}) =>
    ({key, kind, title, subtitle, targetId: id(900 + sort), sportName, seconds, pinned, hidden: false, sort, ...extra});

/**
 * The demo view. `theme`: 0 light, 1 dark. `style`: 0 broadcast, 1 clubhouse.
 */
export const tvDemoView = ({theme = 1, style = 0, accentColour = '#D32946', clubName = 'Riverside Racquets Club'} = {}) => ({
  publicKey: 'demo',
  name: 'Bar screen',
  clubId: id(1),
  clubName,
  clubImageFileName: null,
  disabled: false,
  theme,
  style,
  accentColour,
  defaultPanelSeconds: 12,
  showTicker: true,
  showLiveScores: true,
  celebrateResults: true,
  version: 1,
  generatedAt: new Date().toISOString(),
  panels: [
    panel('splash', TV_PANEL.Splash, clubName, {splash: {activeCompetitions: 5, resultsThisWeek: 14, matchesComingUp: 6, nextMatch: UPCOMING[0]}}, {sort: 0, seconds: 10, sportName: null}),
    panel('live', TV_PANEL.LiveNow, 'Live now', {}, {sort: 1, seconds: 12, sportName: null}),
    panel('standings', TV_PANEL.LeagueStandings, 'Club Championship', {standings: STANDINGS, join: joinCode(7, 'club-championship')}, {sort: 2, subtitle: 'Division 1 · Autumn 2026', pinned: true}),
    panel('ladder', TV_PANEL.Ladder, 'Singles Ladder', {ladder: LADDER}, {sort: 3}),
    panel('draw', TV_PANEL.TournamentDraw, 'Summer Open', {draw: DRAW}, {sort: 4, subtitle: 'Doubles', sportName: 'Padel', seconds: 14}),
    panel('americano', TV_PANEL.AmericanoStandings, 'Thursday Americano', {americano: AMERICANO}, {sort: 5, subtitle: 'Group A · Round 3', sportName: 'Padel'}),
    panel('meet', TV_PANEL.Meet, 'Wednesday Night Mixer', {meet: {occurrenceId: id(601), startsAt: at(1, 19), endsAt: at(1, 21), going: 9, maxAttendees: 12, waitlist: 0}, join: joinCode(11, 'wednesday-mixer')}, {sort: 6, sportName: null}),
    panel('challenges', TV_PANEL.Challenges, 'Ladder challenges', {challenges: CHALLENGES}, {sort: 7, sportName: null}),
    panel('leaderboard', TV_PANEL.Leaderboard, 'Club leaderboard', {stats: STATS}, {sort: 8, sportName: null}),
    panel('grid', TV_PANEL.LeagueGrid, 'Club Championship', {grid: GRID}, {sort: 9, subtitle: 'Division 1 · results grid'}),
    panel('reports', TV_PANEL.MatchReports, 'Match reports', {reports: REPORTS}, {sort: 10, seconds: 20, sportName: null}),
    panel('results', TV_PANEL.RecentResults, 'Latest results', {results: RESULTS}, {sort: 11, sportName: null}),
    panel('upcoming', TV_PANEL.ComingUp, 'Coming up', {upcoming: UPCOMING}, {sort: 12, sportName: null}),
  ],
  ticker: RESULTS,
  live: LIVE,
  clubJoin: joinCode(3, 'riverside'),
});

/** A result to celebrate on demand: the live match, finished. */
export const tvDemoCelebration = () => ({
  result: result(999, 'Chris Dale', 'Lee Walker', [[11, 8], [9, 11], [11, 5], [11, 7]], 'Club Championship · Division 1', 0, 0),
  confirmed: true,
});
